import { NO_TAGS_VALUE } from '../components/v2/filters/filter-primitives.js';
import { matchesTagFilter } from '../pages/v2/phase-board-tag-filter.js';
import {
  type StaffingSortDirection,
  type StaffingSortField,
  sortStaffingItems,
} from './staffing-sort.js';

/**
 * Filterregeln für Projektlisten mit der Staffing-Filterleiste (#55, #56, #81, #114).
 *
 * Genutzt vom Staffing-Tab („Staffing neu") und vom Worklist-Tab der KFML-Worklist.
 * Eine Stelle für die Regeln, damit beide Tabs nicht auseinanderlaufen — genau
 * dieser Drift ist beim Tag-Filter schon einmal passiert (#81).
 */

export type FilterableProject = {
  segment?: string | null | undefined;
  project_lead_user_id?: string | null | undefined;
  manager_user_id?: string | null | undefined;
  tags?:
    | Array<{ tag_id: string; name: string; manager_user_id?: string | null | undefined }>
    | undefined;
  project_key?: string | null | undefined;
  customer_name?: string | null | undefined;
  name?: string | null | undefined;
  kl_followup_date?: string | null | undefined;
};
export type FilterableItem = { project: FilterableProject };

export type ProjectFilterState = {
  segment: string;
  pl: string[];
  manager: string | null;
  tags: string[];
  search: string;
  sortField: StaffingSortField;
  sortDir: StaffingSortDirection;
};

export type Option = { value: string; label: string };
export type ProjectFilterOptions = {
  segment: Option[];
  pl: Option[];
  manager: Option[];
  tags: Option[];
};

const byLabel = (a: Option, b: Option) => a.label.localeCompare(b.label);

/**
 * Optionen immer aus der VOLLEN Liste — sonst schrumpfen sie mit der eigenen Auswahl.
 */
export function buildProjectFilterOptions(
  items: readonly FilterableItem[],
  nameById: ReadonlyMap<string, string>,
  noTagsLabel: string,
): ProjectFilterOptions {
  const segments = new Set<string>();
  const pls = new Set<string>();
  const managers = new Set<string>();
  const tagNames = new Map<string, string>();
  for (const { project: p } of items) {
    if (p.segment) segments.add(p.segment);
    if (p.project_lead_user_id) pls.add(p.project_lead_user_id);
    // Manager = SF-Fachbereichsleiter ODER Tag-Verantwortliche:r (wie Phase Board).
    if (p.manager_user_id) managers.add(p.manager_user_id);
    for (const tg of p.tags ?? []) {
      tagNames.set(tg.tag_id, tg.name);
      if (tg.manager_user_id) managers.add(tg.manager_user_id);
    }
  }
  const person = (id: string): Option => ({ value: id, label: nameById.get(id) ?? id });
  const echteTags = [...tagNames.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort(byLabel);
  return {
    segment: [...segments].sort().map((s) => ({ value: s, label: s })),
    pl: [...pls].map(person).sort(byLabel),
    manager: [...managers].map(person).sort(byLabel),
    // „Keine Tags" nur, wenn es echte Tags gibt (#81) — sonst bleibt der Filter unsichtbar.
    tags:
      echteTags.length === 0 ? [] : [{ value: NO_TAGS_VALUE, label: noTagsLabel }, ...echteTags],
  };
}

export function filterProjectItems<T extends FilterableItem>(
  items: readonly T[],
  state: ProjectFilterState,
  extra?: (item: T) => boolean,
): T[] {
  let list: T[] = [...items];
  if (state.segment) list = list.filter((i) => i.project.segment === state.segment);
  if (extra) list = list.filter(extra);
  // PL-Mehrfachfilter: Projekte ohne PL fallen bei aktivem Filter raus.
  if (state.pl.length > 0) {
    list = list.filter(
      (i) => !!i.project.project_lead_user_id && state.pl.includes(i.project.project_lead_user_id),
    );
  }
  if (state.manager) {
    const m = state.manager;
    list = list.filter(
      (i) =>
        i.project.manager_user_id === m ||
        (i.project.tags ?? []).some((tg) => tg.manager_user_id === m),
    );
  }
  if (state.tags.length > 0)
    list = list.filter((i) => matchesTagFilter(i.project.tags, state.tags));
  const q = state.search.trim().toLowerCase();
  if (q) {
    list = list.filter((i) =>
      `${i.project.project_key ?? ''} ${i.project.customer_name ?? ''} ${i.project.name ?? ''}`
        .toLowerCase()
        .includes(q),
    );
  }
  return sortStaffingItems(list, { field: state.sortField, direction: state.sortDir });
}

export function hasActiveProjectFilters(state: ProjectFilterState): boolean {
  return (
    !!state.segment ||
    state.pl.length > 0 ||
    state.manager !== null ||
    state.tags.length > 0 ||
    state.search.trim() !== ''
  );
}
